(() => {
  if (window.JustNoiseNative) return;
  const controllers = new WeakMap();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function quantityRules(base, maximum, increment, bundle) {
    const step = Math.max(1, Number(increment) || 1);
    const start = Math.max(1, Number(base) || 1);
    const minimum = bundle ? start + Math.max(0, Math.ceil((2 - start) / step)) * step : start;
    const max = Number.isFinite(Number(maximum)) ? Number(maximum) : 999;
    return { min: minimum, max: start + Math.floor((max - start) / step) * step, step };
  }
  function clampQuantity(value, rules) {
    if (rules.max < rules.min) return null;
    const parsed = Number(value);
    const rounded = rules.min + Math.round((Math.max(rules.min, Number.isFinite(parsed) ? parsed : rules.min) - rules.min) / rules.step) * rules.step;
    return Math.min(rules.max, rounded);
  }
  function formatMoney(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency }).format(cents / 100);
  }
  function tabs(root, buttons, activate, signal) {
    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button), { signal });
      button.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        if (event.key === 'ArrowLeft') next = (index + buttons.length - 1) % buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = buttons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        buttons[next].focus();
        activate(buttons[next]);
      }, { signal });
    });
    activate(buttons.find(button => button.getAttribute('aria-selected') === 'true') || buttons[0]);
  }
  function initialize(section) {
    if (controllers.has(section)) return;
    const controller = new AbortController();
    controllers.set(section, controller);
    const signal = controller.signal;
    let editorSelected = false;
    section.addEventListener('shopify:section:select', () => { editorSelected = true; }, { signal });
    section.addEventListener('shopify:section:deselect', () => { editorSelected = false; }, { signal });
    section.addEventListener('shopify:block:select', event => {
      editorSelected = true;
      const item = event.target.closest('.jn-item');
      const slider = item?.closest('css-slider');
      if (slider) slider.changeSlide?.([...slider.querySelectorAll('.jn-item')].indexOf(item), 'auto');
    }, { signal });
    section.addEventListener('shopify:block:deselect', () => { editorSelected = false; }, { signal });
    if (section.hasAttribute('data-jn-faq')) {
      const buttons = [...section.querySelectorAll('[data-jn-category]')];
      const activate = button => {
        if (!button) return;
        buttons.forEach(tab => { tab.setAttribute('aria-selected', String(tab === button)); tab.tabIndex = tab === button ? 0 : -1; });
        section.querySelectorAll('[data-jn-question]').forEach(question => { question.hidden = question.dataset.jnQuestion !== button.dataset.jnCategory; });
      };
      tabs(section, buttons, activate, signal);
      section.addEventListener('shopify:block:select', event => {
        const question = event.target.closest('[data-jn-question]');
        if (!question) return;
        activate(buttons.find(button => button.dataset.jnCategory === question.dataset.jnQuestion));
        question.open = true;
      }, { signal });
    }
    if (section.hasAttribute('data-jn-modes')) {
      const buttons = [...section.querySelectorAll('[data-jn-mode]')];
      const activate = button => {
        if (!button) return;
        buttons.forEach(tab => { tab.setAttribute('aria-selected', String(tab === button)); tab.tabIndex = tab === button ? 0 : -1; });
        section.querySelectorAll('[data-jn-mode-panel]').forEach(panel => {
          panel.hidden = panel.dataset.jnModePanel !== button.dataset.jnMode;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', button.id.replace(button.dataset.jnMode, panel.dataset.jnModePanel));
        });
      };
      tabs(section, buttons, activate, signal);
      section.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-jn-mode-panel]');
        if (panel) activate(buttons.find(button => button.dataset.jnMode === panel.dataset.jnModePanel));
      }, { signal });
    }
    section.querySelectorAll('[data-jn-prev],[data-jn-next]').forEach(button => button.addEventListener('click', () => {
      section.querySelector('css-slider')?.changeSlide(button.hasAttribute('data-jn-prev') ? 'prev' : 'next', reduced.matches ? 'auto' : 'smooth');
    }, { signal }));
    section.querySelectorAll('[data-jn-background-carousel]').forEach(slider => {
      const timer = setInterval(() => {
        if (!reduced.matches && !editorSelected && !section.matches(':hover,:focus-within') && section.getBoundingClientRect().bottom > 0 && section.getBoundingClientRect().top < window.innerHeight) slider.changeSlide?.('next');
      }, 5000);
      signal.addEventListener('abort', () => clearInterval(timer), { once: true });
    });
    const reveals = [...section.querySelectorAll('[data-jn-reveal]')];
    if (reveals.length) {
      for (const paragraph of reveals) {
        if (paragraph.querySelector('.jn-word')) continue;
        const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
        const texts = [];
        while (walker.nextNode()) texts.push(walker.currentNode);
        for (const node of texts) {
          const fragment = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach(word => {
            if (!word.trim()) fragment.append(document.createTextNode(word));
            else { const span = document.createElement('span'); span.className = 'jn-word'; span.textContent = word; fragment.append(span); }
          });
          node.replaceWith(fragment);
        }
      }
      let frame;
      const update = () => {
        frame = null;
        for (const paragraph of reveals) {
          const words = [...paragraph.querySelectorAll('.jn-word')];
          const progress = (window.innerHeight * (window.innerWidth < 768 ? .72 : .68) - paragraph.getBoundingClientRect().top) / (words.length * 6);
          paragraph.classList.toggle('is-enhanced', !reduced.matches);
          words.forEach((word, index) => word.classList.toggle('is-active', reduced.matches || index < Math.max(0, Math.min(1, progress)) * words.length));
        }
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, { signal, passive: true });
      window.addEventListener('resize', schedule, { signal });
      reduced.addEventListener('change', update, { signal });
      signal.addEventListener('abort', () => cancelAnimationFrame(frame), { once: true });
      update();
    }
    section.querySelectorAll('[data-jn-ambient]').forEach(video => {
      const sync = () => { if (reduced.matches) video.pause(); else video.play().catch(() => {}); };
      reduced.addEventListener('change', sync, { signal });
      signal.addEventListener('abort', () => video.pause(), { once: true });
      sync();
    });
    const dialog = section.querySelector('[data-jn-video-dialog]');
    if (dialog) {
      const video = dialog.querySelector('video');
      section.querySelector('[data-jn-video-open]').addEventListener('click', () => { dialog.showModal(); video.play().catch(() => {}); }, { signal });
      dialog.querySelector('[data-jn-video-close]').addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('close', () => video.pause(), { signal });
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal });
      signal.addEventListener('abort', () => { video.pause(); dialog.close(); }, { once: true });
    }
    if (section.querySelector('[data-jn-kingdom-gallery]')) initializeKingdomGallery(section, signal);
    if (section.hasAttribute('data-jn-purchase')) initializePurchase(section, signal);
  }
  function initializeKingdomGallery(section, signal) {
    customElements.whenDefined('product-page').then(() => {
      if (signal.aborted || !section.isConnected) return;
      const gallery = section.querySelector('[data-jn-kingdom-gallery]');
      const slider = gallery.querySelector('css-slider');
      const media = () => [...gallery.querySelectorAll('.product-gallery__item')];
      let selectedIndex = Math.max(0, media().findIndex(item => item.dataset.mediaId === gallery.dataset.initialMediaId));
      const originalChangeSlide = slider.changeSlide;
      // Kingdom owns navigation. This adapter adds reduced motion and editor lifecycle cleanup.
      slider.changeSlide = function (direction, behavior = 'smooth') {
        return originalChangeSlide.call(this, direction, reduced.matches ? 'auto' : behavior);
      };
      for (const [type, listener] of [['resize', section.GALLERY_RESIZE_EVENT], ['scroll', section.GALLERY_NAVIGATION_SCROLL]]) {
        if (!listener) continue;
        window.removeEventListener(type, listener);
        window.addEventListener(type, listener, { signal, passive: true });
      }
      const sync = () => {
        gallery.style.setProperty('--jn-kingdom-media-height', slider.sliderEnabled ? slider.viewport.style.height || 'auto' : 'auto');
        section.querySelectorAll('.product-gallery__thumbnails .thumbnail').forEach(thumbnail => {
          if (thumbnail.classList.contains('active')) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
      };
      const restore = () => {
        ProductGalleryResizeHelper(gallery);
        if (slider.sliderEnabled) slider.changeSlide(selectedIndex, 'auto');
        sync();
      };
      slider.addEventListener('change', () => {
        if (slider.sliderEnabled) selectedIndex = Number(slider.index) || 0;
        sync();
      }, { signal });
      slider.addEventListener('ready', restore, { signal });
      slider.addEventListener('destroy', sync, { signal });
      slider.addEventListener('reset', sync, { signal });
      const sizes = new MutationObserver(sync);
      sizes.observe(slider, { attributes: true, attributeFilter: ['style'], subtree: true });
      window.addEventListener('scroll', sync, { signal, passive: true });
      signal.addEventListener('abort', () => {
        sizes.disconnect();
        slider.changeSlide = originalChangeSlide;
        section._pauseAllMedia();
      }, { once: true });
      restore();
      if (!slider.sliderEnabled && window.innerWidth > 1024 && selectedIndex > 0) {
        window.scrollTo({ top: media()[selectedIndex].offsetTop, behavior: 'auto' });
      }
    });
  }
  function initializePurchase(section, signal) {
    const form = section.querySelector('form');
    const quantity = form.querySelector('[name="quantity"]');
    const offers = [...form.querySelectorAll('[name="properties[the-zap]"]')];
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    const error = form.querySelector('[data-jn-error]');
    const available = section.dataset.available === 'true';
    let busy = false;
    let bundleQuantity = 2;
    async function settleCartEvent(deferred, userError) {
      if (!deferred) return;
      try {
        const response = await fetch(`${window.KROWN.settings.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to refresh the cart state.');
        const cart = window.Shopify.StandardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(await response.json());
        deferred.resolve({ cart, userErrors: userError ? [userError] : [], warnings: [] });
      } catch (failure) {
        deferred.reject(failure);
        window.KROWN.functions.dispatchStandardEvent?.('CartErrorEvent', form.closest('product-form'), { error: failure.message });
      }
    }
    const rules = () => quantityRules(section.dataset.minimum, section.dataset.maximum, section.dataset.increment, offers.find(radio => radio.checked)?.value === '2+ Zap');
    const update = () => {
      const bundle = offers.find(radio => radio.checked)?.value === '2+ Zap';
      const limits = rules();
      const value = clampQuantity(bundle ? quantity.value : 1, limits);
      quantity.min = limits.min;
      quantity.max = limits.max;
      quantity.step = limits.step;
      quantity.value = value ?? limits.min;
      quantity.disabled = value === null;
      section.querySelector('[data-jn-quantity-row]').hidden = !bundle;
      if (bundle && value !== null) bundleQuantity = value;
      buttons.forEach(button => { button.disabled = busy || !available || value === null; });
      section.querySelector('[data-jn-minus]').disabled = value === null || value <= limits.min;
      section.querySelector('[data-jn-plus]').disabled = value === null || value >= limits.max;
      const price = Number(section.dataset.price);
      const multiplier = (100 - Number(section.dataset.discount)) / 100;
      section.querySelector('[data-jn-total]').textContent = formatMoney(Math.round(price * bundleQuantity * multiplier), section.dataset.currency, section.dataset.locale);
      const single = section.querySelector('[data-jn-single-price]');
      if (single) single.textContent = formatMoney(Math.round(price * multiplier), section.dataset.currency, section.dataset.locale);
      const list = section.querySelector('[data-jn-list-price]');
      if (list) list.textContent = formatMoney(price * bundleQuantity, section.dataset.currency, section.dataset.locale);
    };
    offers.forEach(offer => offer.addEventListener('change', () => { quantity.value = offer.value === '2+ Zap' ? bundleQuantity : 1; update(); }, { signal }));
    quantity.addEventListener('change', update, { signal });
    for (const [selector, direction] of [['[data-jn-minus]', -1], ['[data-jn-plus]', 1]]) section.querySelector(selector).addEventListener('click', () => { quantity.value = Number(quantity.value) + direction * rules().step; update(); }, { signal });
    // Keep Kingdom's product form, cart routes and events; capture submission locally for reliable Buy Now errors.
    section.addEventListener('submit', async event => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || !available || !form.reportValidity()) return;
      update();
      if (clampQuantity(quantity.value, rules()) === null) return;
      busy = true;
      buttons.forEach(button => { button.disabled = true; });
      error.hidden = true;
      form.setAttribute('aria-busy', 'true');
      const checkout = event.submitter?.hasAttribute('data-jn-buy') !== false;
      const EventClass = window.Shopify?.StandardEvents?.CartLinesUpdateEvent;
      const deferred = typeof EventClass?.createPromise === 'function' && typeof EventClass?.createCartFromAjaxResponse === 'function' ? EventClass.createPromise() : null;
      let declined = false;
      if (deferred) {
        deferred.promise.catch(() => {});
        window.KROWN.functions.dispatchStandardEvent?.('CartLinesUpdateEvent', form.closest('product-form'), {
          action: 'add', context: 'product',
          lines: [{ merchandiseId: form.elements.namedItem('id').value, quantity: Number(quantity.value) }],
          promise: deferred.promise
        });
      }
      try {
        const body = new FormData(form);
        body.delete('return_to');
        const response = await fetch(`${window.KROWN.settings.routes.cart_add_url}.js`, { method: 'POST', body, headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string' ? result.description : result.message || 'Unable to add Zap. Please check your cart before trying again.';
          declined = true;
          await settleCartEvent(deferred, { code: 'INVALID', field: [], message });
          throw new Error(message);
        }
        await settleCartEvent(deferred);
        window.KROWN.functions.eventDispatcher('krown:cart:add', { productForm: form, variantId: form.elements.namedItem('id').value });
        form.closest('product-form').dispatchEvent(new Event('add-to-cart'));
        const announcement = document.getElementById('screen-reader-info');
        if (announcement) announcement.textContent = window.KROWN.settings.locales?.cart_announcement || 'Zap added to cart.';
        if (checkout) window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
        else if (window.KROWN.settings.cart_action === 'page') window.location.assign(window.KROWN.settings.routes.cart_url);
        else if (typeof window.refreshCart === 'function') { await window.refreshCart(); document.getElementById('site-cart-sidebar')?.show(); }
        else window.location.assign(window.KROWN.settings.routes.cart_url);
      } catch (failure) {
        error.textContent = failure instanceof TypeError ? 'Connection interrupted. Please check your cart before trying again.' : failure.message;
        error.hidden = false;
        if (!declined) {
          deferred?.reject(failure);
          window.KROWN.functions.dispatchStandardEvent?.('CartErrorEvent', form.closest('product-form'), { error: error.textContent });
        }
      } finally {
        busy = false;
        form.removeAttribute('aria-busy');
        update();
      }
    }, { capture: true, signal });
    update();
  }
  function mount(root = document) {
    if (root.matches?.('[data-jn-section]')) initialize(root);
    root.querySelectorAll('[data-jn-section]').forEach(initialize);
  }
  window.JustNoiseNative = { mount, quantityRules, clampQuantity, formatMoney };
  // Kingdom's deferred slider script replaces its markup; bind after all deferred components have initialized.
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', () => mount(), { once: true }); else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-jn-section]')) { controllers.get(event.target)?.abort(); controllers.delete(event.target); }
    event.target.querySelectorAll('[data-jn-section]').forEach(section => { controllers.get(section)?.abort(); controllers.delete(section); });
  });
})();
